import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Shield, ShieldCheck, ShieldAlert, Clock, CheckCircle, XCircle, AlertTriangle,
  FileText, Eye, Search, ChevronLeft, ChevronRight, User, Building, MapPin,
  Calendar, MessageSquare, History, Filter, X as XIcon
} from 'lucide-react';
import { verificationApi } from '../../services/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  unverified: { label: 'Unverified', color: 'bg-slate-100 text-slate-600', icon: Shield },
  documents_submitted: { label: 'Pending Review', color: 'bg-amber-100 text-amber-800', icon: Clock },
  action_required: { label: 'Action Required', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-800', icon: Eye },
  verified_business: { label: 'Verified', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  premium_verified: { label: 'Premium Verified', color: 'bg-purple-100 text-purple-800', icon: ShieldCheck },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  suspended: { label: 'Suspended', color: 'bg-red-200 text-red-900', icon: ShieldAlert },
};

const TABS = [
  { id: 'documents_submitted', label: 'Pending', icon: Clock },
  { id: 'high_risk', label: 'High Risk', icon: AlertTriangle },
  { id: 'action_required', label: 'Action Required', icon: MessageSquare },
  { id: 'expiring', label: 'Expiring Soon', icon: Calendar },
  { id: 'verified_business', label: 'Approved', icon: CheckCircle },
  { id: 'rejected', label: 'Rejected', icon: XCircle },
];

const VerificationAdminPage = () => {
  const [activeTab, setActiveTab] = useState('documents_submitted');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'high_risk') {
        const { data } = await verificationApi.adminQueue({ status: 'all', page, limit: 20 });
        const highRisk = data.users.filter(u => (u.risk_score || 0) >= 2);
        setUsers(highRisk);
        setTotal(highRisk.length);
      } else if (activeTab === 'expiring') {
        const { data } = await verificationApi.adminExpiringDocs(60);
        setExpiringDocs(data.documents || []);
        setUsers([]);
        setTotal(data.total || 0);
      } else {
        const { data } = await verificationApi.adminQueue({ status: activeTab, page, limit: 20 });
        setUsers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const fetchUserDetail = async (userId) => {
    setDetailLoading(true);
    setSelectedUser(userId);
    try {
      const { data } = await verificationApi.adminUserDetail(userId);
      setUserDetail(data);
    } catch (err) {
      toast.error('Failed to load user details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!selectedUser) return;
    if (action === 'reject' && !actionReason.trim()) {
      toast.error('Please provide a reason for rejection.');
      return;
    }
    setActionLoading(true);
    try {
      await verificationApi.adminReview(selectedUser, {
        action,
        notes: actionNotes || undefined,
        reason: actionReason || undefined,
      });
      toast.success(`User ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : action === 'request_info' ? 'notified' : action}d successfully.`);
      setActionNotes('');
      setActionReason('');
      await fetchUserDetail(selectedUser);
      await fetchQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const viewDoc = async (docId) => {
    try {
      const { data } = await verificationApi.viewDocument(docId);
      const byteChars = atob(data.data);
      const byteArray = new Uint8Array([...byteChars].map(c => c.charCodeAt(0)));
      const blob = new Blob([byteArray], { type: data.content_type });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Could not load document.');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredUsers = searchQuery
    ? users.filter(u =>
        (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.company_name || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const u = userDetail?.user;
  const docs = userDetail?.documents || [];
  const audit = userDetail?.audit_log || [];
  const statusCfg = u ? (STATUS_CONFIG[u.trust_status] || STATUS_CONFIG.unverified) : null;

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-[#0A192F]" data-testid="verification-admin-title">Verification Dashboard</h1>
            <p className="text-sm text-slate-500">Review and manage business verification applications</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-slate-200 p-1 rounded-lg mb-5 overflow-x-auto" data-testid="verification-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setPage(1); setSelectedUser(null); setUserDetail(null); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id ? 'bg-[#0A192F] text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex gap-5">
          {/* Left: Queue List */}
          <div className="w-[420px] shrink-0">
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search name, email, company..."
                  className="pl-9 h-9 text-sm"
                  data-testid="verification-search"
                />
              </div>
            </div>

            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white border border-slate-200 p-4 animate-pulse rounded-lg">
                    <div className="h-4 bg-slate-200 rounded w-2/3 mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : activeTab === 'expiring' ? (
              <div className="space-y-2">
                {expiringDocs.length === 0 ? (
                  <div className="bg-white border border-slate-200 p-8 text-center rounded-lg">
                    <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No documents expiring in the next 60 days</p>
                  </div>
                ) : expiringDocs.map(doc => (
                  <div
                    key={doc.doc_id}
                    className="bg-white border border-slate-200 p-3 rounded-lg cursor-pointer hover:border-amber-300"
                    onClick={() => doc.user_id && fetchUserDetail(doc.user_id)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#0A192F] capitalize">{doc.doc_type.replace('_', ' ')}</span>
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]">Expires {formatDate(doc.expiry_date)}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">{doc.user_info?.name} — {doc.user_info?.company_name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.length === 0 ? (
                  <div className="bg-white border border-slate-200 p-8 text-center rounded-lg">
                    <Shield className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No users in this queue</p>
                  </div>
                ) : filteredUsers.map(user => {
                  const sc = STATUS_CONFIG[user.trust_status] || STATUS_CONFIG.unverified;
                  const isSelected = selectedUser === user.user_id;
                  return (
                    <button
                      key={user.user_id}
                      onClick={() => fetchUserDetail(user.user_id)}
                      className={`w-full text-left bg-white border rounded-lg p-3 transition-all ${
                        isSelected ? 'border-[#0A192F] ring-1 ring-[#0A192F]/20' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`queue-user-${user.user_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-[#0A192F]">{user.name}</span>
                        <Badge className={`${sc.color} text-[10px]`}>{sc.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500">{user.company_name || 'No company'} — {user.email}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[10px] text-slate-400 capitalize">{user.user_type}</span>
                        {(user.risk_score || 0) > 0 && (
                          <span className={`text-[10px] font-bold ${user.risk_score >= 3 ? 'text-red-600' : user.risk_score >= 2 ? 'text-amber-600' : 'text-slate-400'}`}>
                            Risk: {user.risk_score}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400">{user.company_country || ''}</span>
                      </div>
                    </button>
                  );
                })}
                {total > 20 && (
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-slate-500">Page {page}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Detail Panel */}
          <div className="flex-1 min-w-0">
            {!selectedUser ? (
              <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                <User className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Select a user from the queue to review their application</p>
              </div>
            ) : detailLoading ? (
              <div className="bg-white border border-slate-200 rounded-lg p-8 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ) : u ? (
              <div className="space-y-4">
                {/* User Header */}
                <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="user-detail-header">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0A192F] rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {u.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <h2 className="font-bold text-[#0A192F]">{u.name}</h2>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    <Badge className={`${statusCfg.color} text-xs`}>{statusCfg.label}</Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Building className="w-3.5 h-3.5 text-slate-400" />
                      {u.company_name || '-'}
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      {u.company_country || u.country || '-'}
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600 capitalize">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {u.user_type}
                    </div>
                  </div>
                  {u.registration_number && (
                    <p className="text-xs text-slate-400 mt-2">Reg #: {u.registration_number}</p>
                  )}
                  {u.trade_countries?.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">Trades with: {u.trade_countries.join(', ')}</p>
                  )}
                </div>

                {/* Risk Flags */}
                {u.risk_flags?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="risk-flags">
                    <h3 className="text-sm font-semibold text-[#0A192F] mb-2 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-500" /> Risk Flags ({u.risk_flags.length})
                    </h3>
                    <div className="space-y-1.5">
                      {u.risk_flags.map((f, i) => (
                        <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded ${
                          f.severity === 'high' ? 'bg-red-50 text-red-700' : f.severity === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-600'
                        }`}>
                          <span className="font-bold uppercase text-[9px] mt-0.5">{f.severity}</span>
                          <span>{f.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="user-documents">
                  <h3 className="text-sm font-semibold text-[#0A192F] mb-3 flex items-center gap-1.5">
                    <FileText className="w-4 h-4" /> Documents ({docs.length})
                  </h3>
                  {docs.length === 0 ? (
                    <p className="text-xs text-slate-400">No documents uploaded.</p>
                  ) : (
                    <div className="space-y-2">
                      {docs.map(doc => (
                        <div key={doc.doc_id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-[#0A192F] capitalize">{doc.doc_type.replace('_', ' ')}</p>
                            <p className="text-[10px] text-slate-400">
                              {doc.filename} — {(doc.size_bytes / 1024).toFixed(0)}KB
                              {doc.expiry_date && <span className="ml-2">Expires: {formatDate(doc.expiry_date)}</span>}
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => viewDoc(doc.doc_id)} className="text-xs h-7">
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="admin-actions">
                  <h3 className="text-sm font-semibold text-[#0A192F] mb-3">Admin Actions</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Notes</label>
                      <textarea
                        value={actionNotes}
                        onChange={e => setActionNotes(e.target.value)}
                        placeholder="Internal notes about this verification..."
                        className="w-full h-16 text-sm border border-slate-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#0A192F]"
                        data-testid="admin-notes"
                      />
                    </div>
                    {u.trust_status !== 'rejected' && (
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Rejection Reason (required for reject)</label>
                        <Input
                          value={actionReason}
                          onChange={e => setActionReason(e.target.value)}
                          placeholder="Reason for rejection or info request..."
                          className="text-sm h-9"
                          data-testid="admin-reason"
                        />
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {u.trust_status !== 'verified_business' && u.trust_status !== 'premium_verified' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleAction('approve')}
                          disabled={actionLoading}
                          data-testid="action-approve"
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {u.trust_status === 'verified_business' && (
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => handleAction('premium_approve')}
                          disabled={actionLoading}
                          data-testid="action-premium"
                        >
                          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Upgrade to Premium
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => handleAction('request_info')}
                        disabled={actionLoading}
                        data-testid="action-request-info"
                      >
                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> Request Info
                      </Button>
                      {u.trust_status !== 'rejected' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleAction('reject')}
                          disabled={actionLoading}
                          data-testid="action-reject"
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      )}
                      {u.trust_status !== 'suspended' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => handleAction('suspend')}
                          disabled={actionLoading}
                          data-testid="action-suspend"
                        >
                          <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Suspend
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction('unsuspend')}
                          disabled={actionLoading}
                        >
                          Unsuspend
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Audit Trail */}
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="audit-trail">
                  <h3 className="text-sm font-semibold text-[#0A192F] mb-3 flex items-center gap-1.5">
                    <History className="w-4 h-4" /> Audit Trail ({audit.length})
                  </h3>
                  {audit.length === 0 ? (
                    <p className="text-xs text-slate-400">No history yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {audit.map((entry, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs border-l-2 border-slate-200 pl-3 py-1">
                          <div className="flex-1">
                            <span className="font-semibold text-[#0A192F] capitalize">{entry.action.replace('_', ' ')}</span>
                            <p className="text-slate-500 mt-0.5">{entry.details}</p>
                            <p className="text-slate-400 text-[10px] mt-0.5">
                              {formatDate(entry.created_at)} — by {entry.admin_id === 'system' ? 'System' : entry.admin_id?.slice(0, 12) + '...'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationAdminPage;
